import { IBooking } from "@/shared/api/aviabooking"

export type CancelUnavailableReason =
    | 'BOOKING_STATUS_NOT_CANCELLABLE'
    | 'REFUND_IN_PROGRESS'
    | 'VOID_WINDOW_EXPIRED'
    | 'PARTNER_ACTION_NOT_ALLOWED'
    | 'BOOKING_UNDER_MANUAL_REVIEW'

export type CancelAvailability = {
    canCancel: boolean
    reasonText: string | null
}

const CancelUnavailableReasonText: Record<CancelUnavailableReason, string> = {
    BOOKING_STATUS_NOT_CANCELLABLE: 'Текущий статус брони не допускает отмену',
    REFUND_IN_PROGRESS: 'По брони уже выполняется возврат',
    VOID_WINDOW_EXPIRED: 'Аннулировать билет можно только в день выписки по МСК',
    PARTNER_ACTION_NOT_ALLOWED: 'Партнёр не разрешает отмену этой брони',
    BOOKING_UNDER_MANUAL_REVIEW: 'Бронь передана на ручной разбор, заявка передана в поддержку',
}

export const formatMskDeadline = (iso: string): string => {
    if (!iso?.trim()) {
        return ''
    }

    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) {
        return ''
    }

    const formatted = new Intl.DateTimeFormat('ru-RU', {
        timeZone: 'Europe/Moscow',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).format(date)

    return `${formatted} (МСК)`
}

// Отсутствие признака и «недоступно без причины» — один и тот же случай на проводе:
// cs-external-api нормализует «avia не прислал признак» в available=false + reason=null,
// и на попытку отмены такая бронь получает aviabooking_cancel_availability_unknown (503).
// Поэтому текст здесь совпадает с тем, что вернёт сервер, если отмену всё же попытаться.
const AVAILABILITY_UNKNOWN_TEXT = 'Сервер не сообщил доступность отмены. Обновите страницу или повторите попытку позже'

export const resolveCancelAvailability = (
    booking: Pick<IBooking, 'admin_cancel_available' | 'admin_cancel_unavailable_reason' | 'admin_cancel_deadline'>
): CancelAvailability => {
    if (booking.admin_cancel_available == null) {
        return {
            canCancel: false,
            reasonText: AVAILABILITY_UNKNOWN_TEXT,
        }
    }

    if (booking.admin_cancel_available === true) {
        return {
            canCancel: true,
            reasonText: null,
        }
    }

    const reason = booking.admin_cancel_unavailable_reason as CancelUnavailableReason | null | undefined
    if (!reason) {
        return {
            canCancel: false,
            reasonText: AVAILABILITY_UNKNOWN_TEXT,
        }
    }

    // Причина есть, но словарю неизвестна — сервер доступность сообщил, просто новым
    // значением: честнее сказать «недоступна», чем «сервер не сообщил».
    const mappedReasonText = CancelUnavailableReasonText[reason]
    let reasonText = typeof mappedReasonText === 'string'
        ? mappedReasonText
        : 'Отмена сейчас недоступна'

    if (reason === 'VOID_WINDOW_EXPIRED' && booking.admin_cancel_deadline) {
        const deadline = formatMskDeadline(booking.admin_cancel_deadline)
        if (deadline) {
            reasonText += `. Срок истёк ${deadline}`
        }
    }

    return {
        canCancel: false,
        reasonText,
    }
}
